import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, FlowArrow, PencilSimple, Trash, CaretRight,
} from "@phosphor-icons/react";

const emptyForm = { name: "", campaign_id: "", description: "", is_active: true };

export default function NurtureSequences() {
  const { currentOrg } = useAuth();
  const [sequences, setSequences] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const featureOff = currentOrg?.enabled_features?.nurture_sequences === false;

  const load = async () => {
    setLoading(true);
    try {
      const [seqRes, campRes] = await Promise.all([
        api.get("/nurture-sequences"),
        api.get("/campaigns").catch(() => ({ data: [] })),
      ]);
      setSequences(seqRes.data || []);
      setCampaigns(campRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sequences");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (!featureOff) load(); /* eslint-disable-next-line */ }, [currentOrg?.org_id]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      campaign_id: s.campaign_id || "",
      description: s.description || "",
      is_active: s.is_active !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        campaign_id: form.campaign_id || null,
        description: form.description || "",
        is_active: !!form.is_active,
      };
      if (editing) {
        await api.patch(`/nurture-sequences/${editing.sequence_id}`, payload);
        toast.success("Sequence updated");
      } else {
        await api.post("/nurture-sequences", payload);
        toast.success("Sequence created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/nurture-sequences/${deleteTarget.sequence_id}`);
      toast.success("Sequence deleted");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (featureOff) {
    return (
      <div className="p-8" data-testid="nurture-sequences-feature-off">
        <div className="rounded-md border border-white/10 bg-graphite-900 p-8 max-w-lg">
          <FlowArrow weight="duotone" className="h-6 w-6 text-ink-secondary" />
          <h1 className="mt-3 font-display text-xl">Nurture Sequences is off</h1>
          <p className="mt-2 text-sm text-ink-secondary">A platform admin can enable it for this workspace.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="nurture-sequences-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary">Workspace</div>
          <h1 className="mt-1 font-display text-3xl tracking-tight">Nurture Sequences</h1>
          <p className="mt-2 text-sm text-ink-secondary max-w-2xl">
            Plan multi-step follow-ups for enrolled leads. This is a planning &amp; manual-checklist layer — no automatic sending happens here.
          </p>
        </div>
        <button
          data-testid="seq-new-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-md bg-white text-graphite-950 hover:bg-neutral-200 px-4 py-2 text-sm font-medium transition-colors"
        >
          <Plus weight="bold" className="h-4 w-4" /> New sequence
        </button>
      </div>

      <div className="rounded-md border border-white/10 overflow-hidden" data-testid="seq-table">
        <div className="grid grid-cols-[1.4fr_1fr_120px_140px_120px] gap-4 px-5 py-3 border-b border-white/10 bg-graphite-950/40 text-[10px] uppercase tracking-[0.18em] text-ink-secondary font-medium">
          <div>Name</div>
          <div>Campaign</div>
          <div>Steps</div>
          <div>Active enrollments</div>
          <div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="px-5 py-3 text-sm text-ink-secondary">Loading…</div>
        ) : sequences.length === 0 ? (
          <div className="px-5 py-3 text-sm text-ink-secondary" data-testid="seq-empty">
            No sequences yet. Create your first follow-up plan.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {sequences.map((s) => (
              <div key={s.sequence_id}
                   data-testid={`seq-row-${s.sequence_id}`}
                   className="grid grid-cols-[1.4fr_1fr_120px_140px_120px] gap-4 px-5 py-3 hover:bg-white/[0.02] items-center">
                <div className="min-w-0">
                  <Link to={`/nurture-sequences/${s.sequence_id}`}
                        data-testid={`seq-link-${s.sequence_id}`}
                        className="text-sm font-medium text-ink-primary hover:text-white flex items-center gap-1.5">
                    {s.name}
                    <CaretRight weight="bold" className="h-3.5 w-3.5 text-ink-secondary" />
                  </Link>
                  {s.description && (
                    <div className="mt-1 text-[11px] text-ink-secondary truncate">{s.description}</div>
                  )}
                  <div className="mt-1 flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${s.is_active ? "border-emerald-400/30 text-emerald-300" : "border-white/15 text-ink-secondary"}`}>
                      {s.is_active ? "Active" : "Paused"}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-ink-primary truncate">{s.campaign_name || "—"}</div>
                <div className="text-sm text-ink-primary" data-testid={`seq-step-count-${s.sequence_id}`}>{s.step_count ?? 0}</div>
                <div className="text-sm text-ink-primary">{s.active_enrollments ?? 0}</div>
                <div className="text-right space-x-1">
                  <button data-testid={`seq-edit-${s.sequence_id}`} onClick={() => openEdit(s)}
                          className="inline-flex items-center rounded-md border border-white/10 hover:bg-white/5 h-8 w-8 justify-center">
                    <PencilSimple className="h-3.5 w-3.5" />
                  </button>
                  <button data-testid={`seq-delete-${s.sequence_id}`} onClick={() => setDeleteTarget(s)}
                          className="inline-flex items-center rounded-md border border-white/10 hover:bg-white/5 hover:text-red-300 h-8 w-8 justify-center">
                    <Trash className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="seq-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit sequence" : "New sequence"}</DialogTitle>
            <DialogDescription>
              A sequence is a planned follow-up. Add steps once created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-secondary">Name *</label>
              <Input data-testid="seq-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={200} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-secondary">Linked campaign</label>
              <Select value={form.campaign_id || "__none__"} onValueChange={(v) => setForm({ ...form, campaign_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="seq-campaign-select"><SelectValue placeholder="No linked campaign" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No linked campaign</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-secondary">Description</label>
              <Textarea data-testid="seq-description-input" value={form.description} rows={3} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={2000} />
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-secondary">
              <input type="checkbox" data-testid="seq-active-input" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active (new leads can be enrolled)
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="rounded-md border border-white/10 px-4 py-2 text-sm hover:bg-white/5">Cancel</button>
            <button data-testid="seq-save-btn" disabled={saving} onClick={save} className="rounded-md bg-white text-graphite-950 hover:bg-neutral-200 px-4 py-2 text-sm font-medium disabled:opacity-50">
              {saving ? "Saving…" : editing ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete sequence?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete "{deleteTarget?.name}" and stop all active enrollments. Existing lead records are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="seq-delete-confirm" onClick={doDelete} className="bg-red-400 text-graphite-950 hover:bg-red-300">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
